import React from 'react';
import { Table, InputGroup, FormControl } from 'react-bootstrap';
import { useTable, useFilters, useGlobalFilter, useSortBy, useResizeColumns, useFlexLayout } from 'react-table';
import styled from 'styled-components';
import tw from 'twin.macro';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;
const Title = tw.h1`text-xl font-bold text-blue-600 mb-2 text-center`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 12px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;
  width: 100%;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}
      
      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }

    .align-right {
      text-align: right;
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const MarginTable = ({ orders }) => {
  const formatAmount = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const columns = React.useMemo(
    () => [
      { Header: 'Exchange Code', accessor: 'limit_list[0].exchange_code', width: 140, align: 'left' },
      { Header: 'Trade Date', accessor: 'limit_list[0].trade_date', width: 110, align: 'right' },
      { Header: 'Amount', accessor: 'limit_list[0].amount', width: 90, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Payin Date', accessor: 'limit_list[0].payin_date', width: 110, align: 'right' },
      { Header: 'Payout Date', accessor: 'limit_list[0].payout_date', width: 110, align: 'right' },
      { Header: 'Cash Limit', accessor: 'cash_limit', width: 90, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Amount Allocated', accessor: 'amount_allocated', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Block by Trade', accessor: 'block_by_trade', width: 130, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'IESC Margin', accessor: 'isec_margin', width: 110, align: 'right', Cell: ({ value }) => formatAmount(value) },
      {
        Header: 'Margin', accessor: 'margin', width: 80, align: 'right',
        Cell: ({ row }) => formatAmount(parseFloat(row.values['limit_list[0].amount']) + parseFloat(row.values.cash_limit))
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    state,
  } = useTable(
    {
      columns,
      data: orders || [],
      initialState: {
        globalFilter: '',
        columnWidths: columns.map(column => column.width), // Set initial column widths
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useFlexLayout
  );

  return (
    <Container>
      <Title>Order List</Title>
      <InputGroup className="my-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      <TableWrapper>
        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <ResizableHeader
                    key={column.id}
                    column={column}
                    className="text-center"
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr key={row.id} {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td 
                      key={cell.id} 
                      {...cell.getCellProps()} 
                      className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </Container>
  );
};

export default MarginTable;
